<template>
    <div class="flex flex-col h-full">
        <div v-if="$slots.top" class="top">
            <slot name="top" />
        </div>
        <div class="content flex-1 p-4" :class="contentClass" :style="contentStyle" ref="content">
            <div v-if="notOnFreePlan">
                <h1 class="text-2xl mb-6">{{ $t('This feature is not included in your free plan.') }}</h1>
                <h2 v-if="freePlanMessage.advice" class="text-xl mb-6">{{ freePlanMessage.advice }}</h2>
                <ul v-if="freePlanMessage.list" class="text-base list-disc pl-4">
                    <li v-for="(item, index) in freePlanMessage.list" :key="index" class="mt-1">{{ item }}</li>
                </ul>
            </div>
            <div
                v-else-if="status === 'loading'"
                class="middle-of-page flex-col text-80">
                    <CircularLoader :percentage="loadingPercentage" />
            </div>
            <div
                v-else-if="status === 'not-found'"
                class="middle-of-page flex-col text-80">
                    <h1 class="text-2xl">{{ $t('Not found') }}</h1>
                    <div class="mt-6 text-center">
                        <button class="btn btn-default btn-primary" @click="backToMyTodos">
                            {{ $t('Back to my todos') }}
                        </button>
                    </div>
            </div>
            <div
                v-else-if="status === 'client-error'"
                class="middle-of-page flex-col text-80">
                    <h1 class="text-2xl">{{ $t('Client Error') }}</h1>
                    {{ error.message }}
                    <div class="mt-6 text-center">
                        <button class="btn btn-default btn-primary mb-6" @click="retry">{{ $t('Retry now') }}</button>
                        <br>
                        or <a href="#" @click="backToMyTodos" class="underline">{{ $t('back to my todos') }}</a>
                    </div>
            </div>
            <div
                v-else-if="status === 'server-error'"
                class="middle-of-page flex-col text-80">
                    <h1 class="text-2xl">{{ $t('Server Error') }}</h1>
                    {{ error.response.status }}: {{ error.response.data.message }}
                    <div class="mt-6 text-center">
                        <button class="btn btn-default btn-primary mb-6" @click="retry">{{ $t('Retry now') }}</button>
                        <br>
                        or <a href="#" @click="backToMyTodos" class="underline">{{ $t('back to my todos') }}</a>
                    </div>
            </div>
            <div
                v-else-if="status === 'offline'"
                class="middle-of-page flex-col text-80">
                    <h1 class="text-2xl">{{ $t('You are offline') }}</h1>
                    <div class="mt-6 text-center">
                        <button class="btn btn-default btn-primary mb-6" @click="retry">{{ $t('Retry now') }}</button>
                        <br>
                        or <a href="#" @click="backToMyTodos" class="underline">{{ $t('back to my todos') }}</a>
                    </div>
            </div>
            <div
                v-else-if="status === 'forbidden'"
                class="middle-of-page flex-col text-80">
                    <h1 class="text-2xl">{{ $t("Forbidden") }}</h1>
                    <div class="mt-6 text-center">
                        <button class="btn btn-default btn-primary mb-6" @click="retry">{{ $t('Retry now') }}</button>
                        <br>
                        or <a href="#" @click="backToMyTodos" class="underline">{{ $t('back to my todos') }}</a>
                    </div>
            </div>
            <div
                v-else-if="status === 'unauthorized'"
                class="middle-of-page flex-col text-80">
                    <h1 class="text-2xl">{{ $t("Unauthorized") }}</h1>
                    <div class="mt-6 text-center">
                        <button class="btn btn-default btn-primary mb-6" @click="login">{{ $t('Login now') }}</button>
                    </div>
            </div>
            <slot v-else />
        </div>
        <div v-if="$slots.bottom" class="bottom">
            <slot name="bottom" />
        </div>
        <modal-free-plan :message="freePlanMessage" v-if="showFreePlanModal" @close="closeFreePlanModal" />
    </div>
</template>

<script>
import CircularLoader from '@mobile/components/CircularLoader.vue';
import ModalFreePlan from '@mobile/components/ModalFreePlan.vue';
import OnFreePlan from '@mobile/mixins/OnFreePlan';
import globals from '@/util/globals';
import { eventBus } from '@/eventBus';

export default {
    components: {
        ModalFreePlan,
        CircularLoader,
    },
    mixins: [OnFreePlan],
    props: {
        contentClass: {
            type: String,
            default: '',
        },
        contentStyle: {
            type: String,
            default: '',
        },
        loader: {
            type: Boolean,
            default: true,
        },
        ignoreOffline: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        modalsOpen: 0,
        error: null,
        status: 'loading',
        loadingPercentage: 0,
        showFreePlanModal: false,
        shouldRedirectOnModalClose: true,
        notOnFreePlan: false,
        freePlanMessage: {},
    }),
    created() {
        this.freePlanMessage.advice = this.$t('Upgrade on desktop to unlock the full potential of Lumiform');
        if (!this.loader) {
            this.status = 'loaded';
        }
        if (!this.ignoreOffline) {
            eventBus.$on('offline', this.offline);
        }
        eventBus.$on('uploadFailed', this.inspectionUploadFailed);
        eventBus.$on('pageFlip', this.scrollTop);
        eventBus.$on('modalOpen', this.modalOpens);
        eventBus.$on('modalClose', this.modalCloses);
        eventBus.$on('client-error', this.clientError);
        eventBus.$on('server-error', this.serverError);
        eventBus.$on('not-found', this.notFound);
        eventBus.$on('forbidden', this.forbidden);
        eventBus.$on('unauthorized', this.unauthorized);
        eventBus.$on('loading', this.loading);
        eventBus.$on('downloadProgress', this.downloadProgress);
        eventBus.$on('loaded', this.loaded);
        eventBus.$on('free-plan-limit', this.showsFreePlanModal);
    },
    beforeDestroy() {
        eventBus.$off('uploadFailed', this.inspectionUploadFailed);
        eventBus.$off('offline', this.offline);
        eventBus.$off('pageFlip', this.scrollTop);
        eventBus.$off('modalOpen', this.modalOpens);
        eventBus.$off('modalClose', this.modalCloses);
        eventBus.$off('client-error', this.clientError);
        eventBus.$off('server-error', this.serverError);
        eventBus.$off('not-found', this.notFound);
        eventBus.$off('forbidden', this.forbidden);
        eventBus.$off('unauthorized', this.unauthorized);
        eventBus.$off('loading', this.loading);
        eventBus.$off('downloadProgress', this.downloadProgress);
        eventBus.$off('loaded', this.loaded);
        eventBus.$off('free-plan-limit', this.showsFreePlanModal);
    },

    watch: {
        '$route.meta': {
            handler: 'setNotOnFreePlan',
            immediate: true,
            deep: true,
        },
    },

    methods: {
        inspectionUploadFailed(message) {
            this.status = 'loaded';
            this.$toasted.show(message, { type: 'error' });
        },
        showsFreePlanModal({ message, shouldRedirect }) {
            if (message) {
                this.freePlanMessage = message;
            }
            this.shouldRedirectOnModalClose = !!shouldRedirect;
            this.showFreePlanModal = true;
        },
        setNotOnFreePlan() {
            this.notOnFreePlan = !!this.$route.meta.notOnFreePlan && this.isOnFreePlan;
            if (this.$route.meta.message !== undefined) {
                this.freePlanMessage = this.$route.meta.message;
            }
        },
        closeFreePlanModal() {
            this.showFreePlanModal = false;
            if (this.shouldRedirectOnModalClose) {
                this.$router.push({ name: 'todos' });
            }
        },
        scrollTop() {
            // https://popmotion.io/blog/20170704-manually-set-scroll-while-ios-momentum-scroll-bounces/
            this.$refs.content.style['-webkit-overflow-scrolling'] = 'auto';
            this.$refs.content.scrollTop = 0;
            this.$refs.content.style['-webkit-overflow-scrolling'] = 'touch';
        },
        modalOpens() {
            if (this.modalsOpen === 0) {
                this.disableScrolling();
            }
            this.modalsOpen += 1;
        },
        modalCloses() {
            this.modalsOpen -= 1;
            if (this.modalsOpen === 0) {
                this.enableScrolling();
            }
        },
        clientError(error) {
            if (this.error !== null) return;
            this.status = 'client-error';
            this.error = error;
        },
        serverError(error) {
            if (this.error !== null) return;
            this.status = 'server-error';
            this.error = error;
        },
        offline() {
            if (this.error !== null) return;
            this.status = 'offline';
            this.error = {};
        },
        notFound() {
            if (this.error !== null) return;
            this.status = 'not-found';
            this.error = {};
        },
        forbidden() {
            if (this.error !== null) return;
            this.status = 'forbidden';
            this.error = {};
        },
        unauthorized() {
            if (this.error !== null) return;
            this.status = 'unauthorized';
            this.error = {};
        },
        loading() {
            this.status = 'loading';
            this.error = null;
        },
        downloadProgress(event) {
            if (this.status !== 'loading') return;
            this.loadingPercentage = event.loaded / event.total * 100;
        },
        loaded() {
            this.status = 'loaded';
            this.error = null;
            this.loadingPercentage = 0;
        },
        retry() {
            this.$nextTick(() => globals.location.reload(true));
        },
        backToMyTodos() {
            this.$router.push({ name: 'todos' }).catch(() => {});
        },
        login() {
            this.$router.push({ name: 'login' }).catch(() => {});
        },
        disableScrolling() {
            this.$refs.content.style['-webkit-overflow-scrolling'] = 'auto';
            this.$refs.content.classList.add('overflow-hidden');
        },
        enableScrolling() {
            this.$refs.content.classList.remove('overflow-hidden');
            this.$refs.content.style['-webkit-overflow-scrolling'] = 'touch';
        },
    },
};
</script>
